import useSWR from 'swr'
import { useAnalytics } from '../components/context/AnalyticsProvider'
import createFormData from './createFormData'
import fetchJson from './fetchJson'
import {
  canEdit,
  canJoin,
  canParticipate,
  MembershipStatus,
} from './policies/groupPolicy'
import useMutators from './useMutators'
import useUser from './useUser'

export default function useGroup(slug, opts = {}) {
  const { trackData } = useAnalytics()
  const { invite } = opts

  let url = null
  if (invite != null) {
    url = `/api/group_invite/${invite}`
  } else if (slug) {
    url = `/api/groups/${slug}.json`
  }

  const {
    error,
    data,
    mutate: mutateGroup,
    isValidating,
    isLoading,
  } = useSWR(url)
  const group = data?.group
  const { user } = useUser()

  const api = useMutators(mutateGroup, {
    updateGroup,
    deleteGroup,
    requestModeratorStatus,
  })

  return {
    group,
    mutateGroup,
    isValidating,
    error,
    isLoading,
    leaveGroup: removeUser.bind(this, group?.id, user?.id),
    removeUser: removeUser.bind(this, group?.id),
    changeRole: changeRole.bind(this, group?.id),
    createGroup: createGroup.bind(this, mutateGroup),
    duplicateGroup: duplicateGroup.bind(this, mutateGroup),
    createGroupMembership: createGroupMembership.bind(
      this,
      group,
      opts,
      trackData
    ),
    updateGroupMembership: updateGroupMembership.bind(this, group),
    blockGroupMembership: blockGroupMembership.bind(this, group),
    unblockGroupMembership: unblockGroupMembership.bind(this, group),
    acceptGroupMembership: acceptGroupMembership.bind(this, group),
    ...api,
    get isManager() {
      return group?.role === 'manager' && group?.status === 'active'
    },
    get isMember() {
      return group?.role === 'member' && group?.status === 'active'
    },
    get canJoin() {
      return canJoin(group?.role, group?.status)
    },
    get canParticipate() {
      return canParticipate(group?.role, group?.status)
    },
    get canEdit() {
      return canEdit(group?.role, group?.status)
    },
    get inviteLink() {
      return `https://${window.location.host}/groups/${slug}?invite=${group?.token}`
    },
    get isBlocked() {
      return MembershipStatus(group?.status).isBlocked
    },
  }
}

export async function updateGroup(group) {
  const formData = createFormData({ group })

  return await fetchJson(`/api/groups/${group.slug}`, {
    method: 'PUT',
    body: formData,
  })
}

export async function createGroup(mutateGroup, group) {
  const formData = createFormData({ group })

  const resp = await fetchJson(`/api/groups`, {
    method: 'POST',
    body: formData,
  })

  mutateGroup((data) => ({ group: { ...data?.group } }))

  return resp
}

export async function duplicateGroup(mutateGroup, data) {
  const resp = await fetchJson(`/api/groups.json`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  })

  mutateGroup((data) => ({ group: { ...data?.group } }))

  return resp
}

export async function removeUser(groupId, userId) {
  return await fetchJson(`/api/users/${userId}/groups/${groupId}`, {
    method: 'DELETE',
  })
}

export async function createGroupMembership(
  group,
  opts,
  trackData = null,
  userId = null,
  status = null,
  data = {}
) {
  if (!userId) {
    throw new Error('userId is required for creating group membership')
  }

  let body = {
    group_id: group?.id,
    invite: opts?.invite,
    user_id: userId,
  }

  if (data) {
    body = {
      ...body,
      group: { ...data },
    }
  }

  if (status === 'invited') {
    body = {
      ...body,
      set_invite_status: true,
    }
  }

  const result = await fetchJson(`/api/users/${userId}/groups.json`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })

  if (status === 'active' && trackData) {
    trackData('group_join', {
      user: { id: userId },
      group,
      joined_at: new Date().toISOString(),
    })
  }

  return result
}

export async function acceptGroupMembership(group, userId = null) {
  return await updateGroupMembership(group, userId, 'active')
}

export async function blockGroupMembership(group, userId = null) {
  return await updateGroupMembership(group, userId, 'blocked')
}

export async function unblockGroupMembership(group, userId = null) {
  return await updateGroupMembership(group, userId, 'active')
}

export async function updateGroupMembership(
  group,
  userId = null,
  status = null
) {
  if (!userId) {
    throw new Error('userId is required for updating group membership')
  }
  if (!status) {
    throw new Error('status is required for updating group membership')
  }

  const body = {
    group_id: group?.id,
    group: { status },
    user_id: userId,
  }

  return await fetchJson(`/api/users/${userId}/groups/${group?.id}.json`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
}

export async function changeRole(groupId, userId, role) {
  return await fetchJson(`/api/users/${userId}/groups/${groupId}.json`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ group: { role } }),
  })
}

export async function requestModeratorStatus(groupId, userId) {
  return await fetchJson(`/api/users/${userId}/groups/${groupId}.json`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ group: { requesting_permission: true } }),
  })
}

export async function deleteGroup(slug) {
  return await fetchJson(`/api/groups/${slug}`, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
  })
}
